import type { SpecFeatureWithDefinition } from './builder-logic';
import type { Feature } from './db/schema';
import type { FeatureDefinition } from './feature-modules/types';

export interface ComposedFeature {
  feature: Feature;
  definition: FeatureDefinition;
  sourceSlugs: string[];
  anyRequired: boolean;
}

interface SourceSpec {
  slug: string;
  specFeatures: Array<{
    feature: Feature;
    isRequired: boolean;
    definition: FeatureDefinition;
  }>;
}

export function mergeComposedFeatures(
  sources: SourceSpec[],
): ComposedFeature[] {
  const bySlug = new Map<string, ComposedFeature>();
  for (const spec of sources) {
    for (const sf of spec.specFeatures) {
      const existing = bySlug.get(sf.feature.slug);
      if (existing) {
        if (!existing.sourceSlugs.includes(spec.slug)) {
          existing.sourceSlugs.push(spec.slug);
        }
        if (sf.isRequired) existing.anyRequired = true;
      } else {
        bySlug.set(sf.feature.slug, {
          feature: sf.feature,
          definition: sf.definition,
          sourceSlugs: [spec.slug],
          anyRequired: sf.isRequired,
        });
      }
    }
  }
  return [...bySlug.values()].sort((a, b) =>
    a.feature.name.localeCompare(b.feature.name),
  );
}

export function toBuilderFeatures(
  composed: ComposedFeature[],
): SpecFeatureWithDefinition[] {
  return composed.map((c) => ({
    feature: { slug: c.feature.slug, name: c.feature.name },
    definition: c.definition,
    isRequired: false,
    defaultPreset: 'standard' as const,
  }));
}
